import { useTranslation } from "react-i18next";
import {
  AdminUsersResponseModel,
  ListAdminUsersQuery,
  useDeleteHaAdminUserFunctionRequest,
  useEraseUserFunctionRequest,
  useListAdminUsersFunctionRequest,
} from "shared/request/myHealthyAdvantageApi";
import { useCallback, useEffect, useState } from "react";
import { OrderByDirection, RoleKeys, Roles } from "core/Constants";
import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { useDebounce } from "shared/core/hooks/useDebounce";
import { ListAdminUsersOrderBy } from "./ListAdminUsersOrderBy";
import { RowMenuItem, Table, TableHeader } from "../../../UIPalette/components/Table/Table";
import { PageHeader } from "../../../UIPalette/components/PageHeader/PageHeader";
import { useNavigate } from "react-router-dom";
import Button from "shared/UI/Buttons/Button";
import "shared/extensions/string";
import { useAdminUserContext } from "core/state/AdminUserContext";
import { ConfirmationModal } from "shared/UI/Modal/ConfirmationModal/ConfirmationModal";
import { Input } from "shared/UI/Input/Input";
import { ReactComponent as SearchIcon } from "@icons/magnifying-glass.svg";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";

export const ViewWWAdmins = () => {
  const { t } = useTranslation();
  const { modalNotify } = useNotifyModalContext();
  const { trigger: listAdminUsersRequest } = useListAdminUsersFunctionRequest();
  const { trigger: deleteAdminUserRequest } = useDeleteHaAdminUserFunctionRequest();
  const { trigger: eraseUserRequest } = useEraseUserFunctionRequest();
  const [userResponse, setUserResponse] = useState<AdminUsersResponseModel>();
  const [errorMessage, setErrorMessage] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdminUserIndex, setSelectedAdminUserIndex] = useState(0);
  const [ordering, setOrdering] = useState({
    column: ListAdminUsersOrderBy.CreatedDateUtc,
    direction: OrderByDirection.Descending,
  });
  const [paging, setPaging] = useState({ current: 1, size: 10 });
  const debouncedSearchFilter = useDebounce(searchFilter, 500);
  const navigate = useNavigate();
  const { isAuthorised } = useAdminUserContext();
  const [showEraseModal, setShowEraseModal] = useState(false);

  const tableHeaders: TableHeader[] = [
    {
      text: t("wwAdminManagement.table.nameHeading"),
      orderByValue: ListAdminUsersOrderBy.Forenames,
    },
    {
      text: t("wwAdminManagement.table.surnameHeading"),
      orderByValue: ListAdminUsersOrderBy.Surname,
    },
    { text: t("wwAdminManagement.table.emailHeading"), orderByValue: ListAdminUsersOrderBy.Email },
    {
      text: t("wwAdminManagement.table.statusHeading"),
      orderByValue: ListAdminUsersOrderBy.IsActive,
    },
    {
      text: t("wwAdminManagement.table.creationDateHeading"),
      orderByValue: ListAdminUsersOrderBy.CreatedDateUtc,
      defaultOrderDirection: OrderByDirection.Descending,
    },
  ];

  const buildRowMenuItems = () => {
    let rowMenuItems: RowMenuItem[] = [];

    if (isAuthorised({ role: RoleKeys.HaAdmin, permissions: ["delete-ha-admins"] })) {
      rowMenuItems.push({
        title: t("table.deleteTitle"),
        "aria-label": t("table.ariaDeleteRow"),
        onClick: (rowIndex: number) => onDeleteAdminUser(rowIndex),
      });
    }

    if (isAuthorised({ role: RoleKeys.HaAdmin, permissions: ["edit-ha-admins"] })) {
      rowMenuItems.push({
        title: t("table.editTitle"),
        "aria-label": t("table.ariaEditRow"),
        onClick: (rowIndex: number) => onEditAdminUser(rowIndex),
      });
    }

    if (isAuthorised({ role: RoleKeys.HaAdmin, permissions: ["erase-users"] })) {
      rowMenuItems.push({
        title: t("table.eraseTitle"),
        "aria-label": t("table.ariaEraseRow"),
        onClick: (row: number) => onEraseAdminUser(row),
      });
    }

    return rowMenuItems;
  };

  const mapTableData = () =>
    userResponse?.records?.map(({ forenames, surname, email, isActive, ...rest }) => {
      return {
        forenames,
        surname,
        email,
        status: isActive ? t("wwAdminManagement.status.active") : t("wwAdminManagement.status.pending"),
        createdDate: String.formatDateTime(rest.createdDate),
      };
    });

  const deleteAdminUser = async () => {
    try {
      const userId = userResponse!.records![selectedAdminUserIndex].id;
      await deleteAdminUserRequest({ query: { id: userId } });
      fetchListAdminUsers();
      modalNotify("success", t("adminDelete.successTitle"), t("adminDelete.successMessage"));
    } catch {
      setErrorMessage(t("adminDelete.ww.requestErrorMessage"));
    } finally {
      setShowDeleteModal(false);
    }
  };

  const eraseUser = async () => {
    try {
      const targetUser = userResponse!.records![selectedAdminUserIndex];
      await eraseUserRequest({ body: { email: targetUser.email } });
      fetchListAdminUsers();
      modalNotify("success", t("eraseModal.successfulTitle"), t("eraseModal.successfulErase"));
    } catch {
      setErrorMessage(t("eraseModal.failedErase"));
    } finally {
      setShowEraseModal(false);
    }
  };

  const fetchListAdminUsers = useCallback(() => {
    const requestBody: ListAdminUsersQuery = {
      roleId: Roles.HaAdmin,
      page: paging.current,
      perPage: paging.size,
      orderBy: ordering.column,
      orderByDirection: ordering.direction,
      filter: {
        keyword: debouncedSearchFilter,
      },
    };

    async function listAdminUsers() {
      try {
        setErrorMessage("");
        const response = await listAdminUsersRequest({ body: requestBody });
        setUserResponse(response?.data);
      } catch {
        setErrorMessage(t("wwAdminManagement.requestErrorMessage"));
      }
    }

    listAdminUsers();
  }, [debouncedSearchFilter, listAdminUsersRequest, ordering.column, ordering.direction, paging, t]);

  useEffect(() => {
    fetchListAdminUsers();
  }, [fetchListAdminUsers]);

  const onDeleteAdminUser = async (rowIndex: number) => {
    setSelectedAdminUserIndex(rowIndex);
    setShowDeleteModal(true);
  };

  const onEraseAdminUser = async (rowIndex: number) => {
    setSelectedAdminUserIndex(rowIndex);
    setShowEraseModal(true);
  };

  const onEditAdminUser = async (rowIndex: number) => {
    navigate("/usermanagement/wwadmins/edit", { state: userResponse!.records![rowIndex] });
  };

  return (
    <>
      <PageHeader title={t("wwAdminManagement.title")} subtitle={t("wwAdminManagement.subtitle")} />
      <div className="w-2/5 my-2 content-center">
        <Input
          aria-label={t("wwAdminManagement.ariaSearch")}
          onChange={(a) => setSearchFilter(a.target.value)}
          icon={<SearchIcon />}
          placeholder={t("wwAdminManagement.searchPlaceholder")}
          label={t("wwAdminManagement.searchLabel")}
        />
      </div>

      <Table
        headers={tableHeaders}
        dataRows={mapTableData() ?? []}
        orderedByColumn={ordering.column}
        orderedDirection={ordering.direction}
        currentPage={paging.current}
        pageSize={paging.size}
        totalDataCount={userResponse?.metadata?.totalCount ?? 0}
        onOrderingChange={setOrdering}
        onPagingChange={setPaging}
        actionButtons={<Button text={"+"} onClick={() => navigate("/usermanagement/wwadmins/create")} />}
        rowMenuItems={buildRowMenuItems()}
      />
      {showDeleteModal && (
        <ConfirmationModal
          title={t("adminDelete.ww.modalTitle")}
          message={t("adminDelete.modalMessage", { userEmail: userResponse!.records![selectedAdminUserIndex].email })}
          cancelButtonText={t("adminDelete.modalDismissTitle")}
          confirmButtonText={t("adminDelete.modalConfirmTitle")}
          onCancel={() => {
            setShowDeleteModal(false);
          }}
          onConfirm={deleteAdminUser}
        />
      )}
      {showEraseModal && (
        <ConfirmationModal
          title={t("eraseModal.title")}
          message={t("eraseModal.message", { userEmail: userResponse!.records![selectedAdminUserIndex].email })}
          cancelButtonText={t("eraseModal.cancel")}
          confirmButtonText={t("eraseModal.confirm")}
          onCancel={() => {
            setShowEraseModal(false);
          }}
          onConfirm={eraseUser}
        />
      )}
      <ErrorAlert content={errorMessage} />
    </>
  );
};
